import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, ArrowRight, Tag, Layers, Clock, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { OptimizedImage } from './OptimizedImage';
import { cleanTitle } from './ProductCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const safePrice = (v) => { const n = Number(v); return Number.isFinite(n) ? n.toFixed(2) : '0.00'; };

const RECENT_KEY = 'ss_recent_searches';
const MAX_RECENT = 5;

const getRecent = () => {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').slice(0, MAX_RECENT); } catch { return []; }
};
const saveRecent = (term) => {
  const list = getRecent().filter(t => t.toLowerCase() !== term.toLowerCase());
  list.unshift(term);
  localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, MAX_RECENT)));
};

const POPULAR = ['Leotards', 'Tights', 'Dance Shoes', 'Leggings', 'Yoga'];

export const SmartSearch = ({ onClose }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ products: [], brands: [], categories: [], suggestions: [] });
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [recent] = useState(getRecent);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const doSearch = useCallback(async (q) => {
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      setResults({ products: [], brands: [], categories: [], suggestions: [] });
      setActiveIdx(-1);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/predictive-search?q=${encodeURIComponent(trimmed)}&limit=8`);
      setResults(res.data);
      setActiveIdx(-1);
    } catch {
      setResults({ products: [], brands: [], categories: [], suggestions: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  // Focus trap inside search overlay
  useEffect(() => {
    const overlay = document.querySelector('[data-testid="smart-search-overlay"]');
    if (!overlay) return;
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      const focusable = overlay.querySelectorAll('a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])');
      if (focusable.length === 0) return;
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleInput = (val) => {
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 350);
  };

  const goTo = (path, term) => {
    if (term) saveRecent(term);
    navigate(path);
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const t = query.trim();
    if (t) goTo(`/shop?search=${encodeURIComponent(t)}`, t);
  };

  /* Flat list of navigable items for keyboard */
  const flatItems = [];
  results.brands.forEach(b => flatItems.push({ type: 'brand', data: b }));
  results.categories.forEach(c => flatItems.push({ type: 'category', data: c }));
  results.products.forEach(p => flatItems.push({ type: 'product', data: p }));
  if (flatItems.length > 0) flatItems.push({ type: 'viewall' });

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (flatItems.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, flatItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      const item = flatItems[activeIdx];
      if (item.type === 'brand') goTo(`/shop?brand=${encodeURIComponent(item.data.id)}`, query.trim());
      else if (item.type === 'category') goTo(`/shop?category=${encodeURIComponent(item.data.id)}`, query.trim());
      else if (item.type === 'product') goTo(`/product/${item.data.id}`, query.trim());
      else if (item.type === 'viewall') goTo(`/shop?search=${encodeURIComponent(query.trim())}`, query.trim());
    }
  };

  const hasResults = results.products.length > 0 || results.brands.length > 0 || results.categories.length > 0;
  const showDropdown = query.trim().length >= 2;
  const showIdle = !showDropdown;

  let flatIdx = 0;

  return (
    <div className="fixed inset-0 z-[100] flex flex-col" data-testid="smart-search-overlay" role="dialog" aria-label="Search" aria-modal="true">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />

      {/* Search panel */}
      <div className="relative bg-white w-full max-w-2xl mx-auto mt-0 sm:mt-16 sm:rounded-lg shadow-2xl overflow-hidden animate-in fade-in slide-in-from-top-4 duration-200 flex flex-col max-h-[100dvh] sm:max-h-[80vh]">
        {/* Input area */}
        <form onSubmit={handleSubmit} className="flex items-center border-b border-[#E5D8D0] px-4">
          <Search className="w-5 h-5 text-[#999] flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => handleInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search products, brands, categories..."
            className="flex-1 py-4 px-3 text-base bg-transparent outline-none placeholder:text-[#bbb]"
            data-testid="search-input"
            autoComplete="off"
            autoFocus
            aria-label="Search products, brands, categories"
          />
          {query && (
            <button
              type="button"
              onClick={() => { setQuery(''); setResults({ products: [], brands: [], categories: [] }); inputRef.current?.focus(); }}
              className="p-1.5 text-[#999] hover:text-[#333] transition-colors"
              data-testid="search-clear"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="ml-2 text-xs text-[#999] hover:text-[#000] transition-colors sm:hidden"
            data-testid="search-close-mobile"
          >
            Cancel
          </button>
        </form>

        {/* Results area */}
        <div ref={listRef} className="overflow-y-auto flex-1 overscroll-contain" data-testid="search-results-dropdown">

          {/* Idle state: recent + popular */}
          {showIdle && (
            <div className="p-4 space-y-5">
              {recent.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[#999] font-medium mb-2 flex items-center gap-1.5">
                    <Clock className="w-3 h-3" /> Recent
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {recent.map(t => (
                      <button
                        key={t}
                        onClick={() => { setQuery(t); doSearch(t); }}
                        className="px-3 py-1.5 text-sm border border-[#E5D8D0] hover:border-[#000] hover:bg-[#000] hover:text-white transition-colors"
                        data-testid={`recent-search-${t}`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] text-[#999] font-medium mb-2 flex items-center gap-1.5">
                  <TrendingUp className="w-3 h-3" /> Popular
                </p>
                <div className="flex flex-wrap gap-2">
                  {POPULAR.map(t => (
                    <button
                      key={t}
                      onClick={() => { setQuery(t); doSearch(t); }}
                      className="px-3 py-1.5 text-sm border border-[#E5D8D0] hover:border-[#000] hover:bg-[#000] hover:text-white transition-colors"
                      data-testid={`popular-search-${t}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Loading */}
          {loading && !hasResults && showDropdown && (
            <div className="px-4 py-8 text-center text-sm text-[#999]">Searching...</div>
          )}

          {/* Brand matches */}
          {results.brands.length > 0 && (
            <div className="border-b border-[#E5D8D0]">
              <p className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.15em] text-[#999] font-medium">Brands</p>
              {results.brands.map(b => {
                const idx = flatIdx++;
                return (
                  <button
                    key={b.id}
                    onClick={() => goTo(`/shop?brand=${encodeURIComponent(b.id)}`, query.trim())}
                    className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors ${activeIdx === idx ? 'bg-[#F7F7EB]' : 'hover:bg-[#F7F7EB]'}`}
                    data-testid={`search-brand-${b.id}`}
                  >
                    <Tag className="w-3.5 h-3.5 text-[#DEA193] flex-shrink-0" />
                    <span className="text-sm font-medium">{b.name}</span>
                    <span className="text-[10px] text-[#bbb] ml-1">{b.product_count} products</span>
                    <ArrowRight className="w-3 h-3 text-[#ccc] ml-auto" />
                  </button>
                );
              })}
            </div>
          )}

          {/* Category matches */}
          {results.categories.length > 0 && (
            <div className="border-b border-[#E5D8D0]">
              <p className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.15em] text-[#999] font-medium">Categories</p>
              {results.categories.map(c => {
                const idx = flatIdx++;
                return (
                  <button
                    key={c.id}
                    onClick={() => goTo(`/shop?category=${encodeURIComponent(c.id)}`, query.trim())}
                    className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors ${activeIdx === idx ? 'bg-[#F7F7EB]' : 'hover:bg-[#F7F7EB]'}`}
                    data-testid={`search-category-${c.id}`}
                  >
                    <Layers className="w-3.5 h-3.5 text-[#DEA193] flex-shrink-0" />
                    <span className="text-sm">{c.name}</span>
                    <span className="text-[10px] text-[#bbb] ml-1">{c.product_count} products</span>
                    <ArrowRight className="w-3 h-3 text-[#ccc] ml-auto" />
                  </button>
                );
              })}
            </div>
          )}

          {/* Product results */}
          {results.products.length > 0 && (
            <div>
              <p className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.15em] text-[#999] font-medium">Products</p>
              {results.products.map(p => {
                const idx = flatIdx++;
                const isSale = p.on_sale && p.sale_price && p.sale_price < p.price;
                return (
                  <button
                    key={p.id}
                    onClick={() => goTo(`/product/${p.id}`, query.trim())}
                    className={`w-full text-left px-4 py-2.5 flex items-center gap-3 transition-colors group ${activeIdx === idx ? 'bg-[#F7F7EB]' : 'hover:bg-[#F7F7EB]'}`}
                    data-testid={`search-result-${p.id}`}
                  >
                    <div className="w-10 h-[3.25rem] bg-[#F5F5F5] overflow-hidden flex-shrink-0">
                      {p.image ? (
                        <OptimizedImage src={p.image} alt={p.name} className="w-full h-full object-contain" width={40} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-[#F0EFE7]">
                          <svg className="w-3.5 h-3.5 text-[#C4B5A8]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-[#999] mb-0.5 uppercase">{p.brand}</p>
                      <p className="text-sm font-medium truncate group-hover:text-[#DEA193] transition-colors">{cleanTitle(p.name, p.brand, p.colors)}</p>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      {isSale ? (
                        <div>
                          <span className="text-sm font-medium text-[#DEA193]">£{safePrice(p.sale_price)}</span>
                          <span className="text-[10px] text-[#999] line-through block">£{safePrice(p.price)}</span>
                        </div>
                      ) : (
                        <span className="text-sm font-medium">£{safePrice(p.price)}</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* No results */}
          {!loading && !hasResults && showDropdown && (
            <div className="px-4 py-10 text-center" data-testid="search-no-results">
              <p className="text-sm text-[#999]">No results for "<span className="text-[#000] font-medium">{query}</span>"</p>
              {results.suggestions && results.suggestions.length > 0 ? (
                <div className="mt-4" data-testid="search-suggestions">
                  <p className="text-xs text-[#999] mb-2.5">Did you mean...</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {results.suggestions.map(s => (
                      <button
                        key={s}
                        onClick={() => { setQuery(s); doSearch(s); }}
                        className="px-4 py-2 text-sm font-medium border border-[#DEA193] text-[#DEA193] hover:bg-[#DEA193] hover:text-white transition-colors"
                        data-testid={`search-suggestion-${s}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-xs text-[#bbb] mt-1">Try a different search term or browse categories</p>
              )}
            </div>
          )}

          {/* View all results */}
          {hasResults && showDropdown && (() => {
            const idx = flatIdx++;
            return (
              <button
                onClick={() => goTo(`/shop?search=${encodeURIComponent(query.trim())}`, query.trim())}
                className={`w-full px-4 py-3 text-sm text-[#DEA193] transition-colors border-t border-[#E5D8D0] flex items-center justify-center gap-2 font-medium ${activeIdx === idx ? 'bg-[#F7F7EB]' : 'hover:bg-[#F7F7EB]'}`}
                data-testid="search-view-all"
              >
                View all results for "{query.trim()}" <ArrowRight className="w-3.5 h-3.5" />
              </button>
            );
          })()}
        </div>
      </div>
    </div>
  );
};
